import './Toggle.css'
import type { FocusEvent, MouseEvent, TouchEvent } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

function pointerCoord(
  event: TouchEvent<HTMLDivElement> | MouseEvent<HTMLDivElement>
) {
  // get coordinates for either a mouse click
  // or a touch depending on the given event
  if (event) {
    const changedTouches =
      'changedTouches' in event ? event.changedTouches : null
    if (changedTouches && changedTouches.length > 0) {
      const touch = changedTouches[0]
      if (!touch) {
        return { x: 0, y: 0 }
      }
      return { x: touch.clientX, y: touch.clientY }
    }
    const pageX = 'pageX' in event ? event.pageX : null
    const pageY = 'pageY' in event ? event.pageY : null
    if (pageX && pageY) {
      return { x: pageX, y: pageY }
    }
  }
  return { x: 0, y: 0 }
}

export type ToggleProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> & {
  labels?:
    | {
        checked?: React.ReactNode
        unchecked?: React.ReactNode
      }
    | boolean
  size?: 'small' | 'medium'
}

export const Toggle: React.FC<ToggleProps> = (props) => {
  const { className, labels, size, ...inputProps } = props
  const { checked, defaultChecked, disabled, onFocus, onBlur } = props
  const icons = labels ? labels : undefined
  const [isChecked, setIsChecked] = useState(!!(checked || defaultChecked))
  const [hasFocus, setHasFocus] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const previouslyChecked = useRef(isChecked)
  const startX = useRef<number | null>(null)
  const activated = useRef(false)
  const moved = useRef(false)

  useEffect(() => {
    if (checked !== undefined) {
      setIsChecked(checked)
    }
  }, [checked])

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      return
    }

    const checkbox = inputRef.current
    if (!checkbox) {
      return
    }

    if (event.target !== checkbox && !moved.current) {
      previouslyChecked.current = checkbox.checked
      event.preventDefault()
      checkbox.focus()
      checkbox.click()
      return
    }

    const updatedChecked = checked !== undefined ? checked : checkbox.checked
    setIsChecked(updatedChecked)
  }

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (disabled) {
      return
    }
    startX.current = pointerCoord(event).x
    activated.current = true
  }

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (!activated.current) {
      return
    }
    moved.current = true

    const currentX = pointerCoord(event).x
    if (startX.current !== null) {
      if (isChecked && currentX + 15 < startX.current) {
        setIsChecked(false)
        startX.current = currentX
      } else if (currentX - 15 > startX.current) {
        setIsChecked(true)
        startX.current = currentX
        activated.current = currentX < startX.current + 5
      }
    }
  }

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (!moved.current) {
      return
    }

    const checkbox = inputRef.current
    if (!checkbox || startX.current === null) {
      return
    }

    const endX = pointerCoord(event).x
    event.preventDefault()

    if (previouslyChecked.current === true && startX.current + 4 > endX) {
      if (previouslyChecked.current !== isChecked) {
        setIsChecked(false)
        previouslyChecked.current = isChecked
        checkbox.click()
      }
    } else if (startX.current - 4 < endX) {
      if (previouslyChecked.current !== isChecked) {
        setIsChecked(true)
        previouslyChecked.current = isChecked
        checkbox.click()
      }
    }

    activated.current = false
    startX.current = null
    moved.current = false
  }

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setHasFocus(true)
    if (onFocus) {
      onFocus(event)
    }
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setHasFocus(false)
    if (onBlur) {
      onBlur(event)
    }
  }

  const getIcon = (type: 'checked' | 'unchecked'): React.ReactNode | null => {
    if (!icons || typeof icons === 'boolean') {
      return null
    }
    return icons[type]
  }

  const classes = classNames(
    'react-toggle',
    {
      'react-toggle--checked': isChecked,
      'react-toggle--focus': hasFocus,
      'react-toggle--disabled': disabled,
      'react-toggle--labelled': Boolean(labels),
      small: size === 'small',
    },
    className
  )

  return (
    <div
      className={classes}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="react-toggle-track">
        <div className="react-toggle-track-check">{getIcon('checked')}</div>
        <div className="react-toggle-track-x">{getIcon('unchecked')}</div>
      </div>
      <div className="react-toggle-thumb" />
      <input
        {...inputProps}
        ref={inputRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="react-toggle-screenreader-only"
        type="checkbox"
        disabled={disabled}
      />
    </div>
  )
}
